import React from 'react'
import { gql, useQuery } from '@apollo/client'
import { LandscapeRowCollection } from '@/molecules/LandscapeRowCollection'
import { DiscoverItemInteraction } from '@/services/Analytics/ContentInteractions'
import { createWebClient } from '@/services/ApolloClient'
import { useGuildMember } from '@/services/GuildUserService'
import { useUser } from '@/services/UserService'
import { GetTopPicksForYouQuery } from '@/types/codegen-federation'
import { buildLinkToEpisode } from '@/utils/EpisodeUtil'
import { useLocale } from '@/utils/LocaleUtil'
import { isDefined } from '@/utils/primitives/generics'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { TopPickCard, TopPicksLinkViewModel } from './TopPickCard'

interface TopPicksForYouCollectionProps {
  containerPosition?: number
}

export function TopPicksForYouCollection({ containerPosition = 0 }: TopPicksForYouCollectionProps) {
  const { userId, isLoggedIn } = useUser()
  // TODO: when web implements user profiles, use that profile id here instead.
  const viewerProfileId = userId ?? undefined
  const { isGuildMember, loading } = useGuildMember()
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const [contentLinks, setContentLinks] = React.useState<TopPicksLinkViewModel[]>([])

  // Hover tracking refs
  const hoverStartTimeRef = React.useRef<Map<string, number>>(new Map())
  const impressionTimeoutRef = React.useRef<Map<string, NodeJS.Timeout>>(new Map())
  const hasTrackedImpression = React.useRef<Set<string>>(new Set())

  const client = React.useMemo(() => {
    return createWebClient({ locale, viewerProfileId })
  }, [locale, viewerProfileId])

  const createInteraction = React.useCallback(
    (contentLink: TopPicksLinkViewModel, itemIndex: number) => {
      const contentId = contentLink.id ?? ''
      if (!contentId || !contentLink.__typename || !contentLink.imageUrl) return null

      return new DiscoverItemInteraction({
        id: contentId,
        __typename: contentLink.__typename as
          | 'ContentEpisode'
          | 'ContentLivestream'
          | 'ContentSeries'
          | 'ContentMovie'
          | 'ContentSpecial',
        attribution: contentLink.attribution,
      })
        .withSurface('discover')
        .withPlacement('start_watching', containerPosition, itemIndex, 'horizontal_swimlane')
        .withImageArtwork(contentLink.imageUrl, 'landscape_display')
    },
    [containerPosition],
  )

  useQuery<GetTopPicksForYouQuery>(GET_TOP_PICKS_FOR_YOU_QUERY, {
    client,
    errorPolicy: 'all',
    skip: !isLoggedIn,
    onCompleted: (data) => {
      const links = data?.startWatching?.content?.map(mapContentLink.bind(null, t)).filter(isDefined) ?? []

      setContentLinks(links)
    },
  })

  if (contentLinks.length < 4) return null

  return (
    <LandscapeRowCollection
      testId="top-picks-for-you-collection"
      title={t('topPicksForYou', 'Top Picks For You')}
      className="relative"
    >
      {contentLinks.map((contentLink, itemIndex) => (
        <TopPickCard
          key={contentLink.imageUrl}
          contentLink={contentLink}
          itemIndex={itemIndex}
          isGuildMember={isGuildMember}
          loading={loading}
          hoverStartTimeRef={hoverStartTimeRef}
          impressionTimeoutRef={impressionTimeoutRef}
          hasTrackedImpression={hasTrackedImpression}
          createInteraction={createInteraction}
        />
      ))}
    </LandscapeRowCollection>
  )
}

const GET_TOP_PICKS_FOR_YOU_QUERY = gql`
  query getTopPicksForYou {
    startWatching {
      content {
        id
        title {
          ... on ContentSeries {
            id
            project {
              slug
            }
          }
          ... on ContentMovie {
            id
            project {
              id
              slug
            }
          }
          ... on ContentDisplayable {
            id
            name
            image(aspect: "16:9", category: ANGEL_KEY_ART_2) {
              cloudinaryPath
            }
          }
          ... on ContentWatchableAvailability {
            watchableAt
            watchableAvailabilityStatus
            actionsToWatch {
              __typename
            }
            watchableReasons {
              __typename
              ... on LinkshareAvailabilityAction {
                startDatetime
              }
            }
          }
        }
        watchable {
          __typename
          ... on Node {
            id
          }
          ... on ContentDisplayable {
            id
            name
          }
          ... on ContentEpisode {
            id
            name
            subtitle
          }
          ... on ContentWatchableAvailability {
            watchableAt
            watchableAvailabilityStatus
            actionsToWatch {
              __typename
            }
            watchableReasons {
              __typename
              ... on LinkshareAvailabilityAction {
                startDatetime
              }
            }
          }
        }
      }
    }
  }
`

type QueryContent = NonNullable<NonNullable<GetTopPicksForYouQuery['startWatching']>['content']>[number]
type QueryEpisode = QueryContent & { watchable: { __typename: 'ContentEpisode' } }
type QuerySpecial = QueryContent & { watchable: { __typename: 'ContentSpecial' } }
type QueryMovie = QueryContent & { watchable: { __typename: 'ContentMovie' } }

function mapContentLink(t: TranslateFunction, content: QueryContent): TopPicksLinkViewModel | undefined {
  if (!content) return undefined

  const imageUrl = content.title?.image?.cloudinaryPath
  if (!imageUrl) return undefined

  return {
    id: content?.title?.id ?? content.id ?? undefined,
    imageUrl,
    watchableAt:
      content?.title && content?.title && 'watchableAt' in content.title
        ? content?.title?.watchableAt
        : content?.watchable?.watchableAt ?? null,
    eyebrow: formatEyebrow(content, t),
    subtitle: formatSubtitle(content),
    linkUrl: buildLinkToEpisode({
      projectSlug: content.title?.project.slug as string,
      guid: content.watchable?.id as string,
    }),
    __typename: content?.watchable?.__typename ?? null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    attribution: (content as any)?.attribution,
  }
}

function formatEyebrow(content: QueryContent, t: TranslateFunction) {
  if (!content) return undefined

  if (content.title?.name?.toLowerCase() === 'minisode') {
    return t('minisode', 'Minisode')
  } else if (isEpisode(content)) {
    return t('seriesDescription', 'series')
  } else if (isSpecial(content)) {
    return t('special', 'Special')
  } else if (isMovie(content)) {
    return t('film', 'Film')
  }
}

function formatProjectTitle(content: QueryContent) {
  return content?.title?.name ?? undefined
}

function formatSubtitle(content: QueryContent) {
  return formatProjectTitle(content)
}

function isEpisode(content: QueryContent): content is QueryEpisode {
  return content?.watchable?.__typename === 'ContentEpisode'
}

function isSpecial(content: QueryContent): content is QuerySpecial {
  return content?.watchable?.__typename === 'ContentSpecial'
}

function isMovie(content: QueryContent): content is QueryMovie {
  return content?.watchable?.__typename === 'ContentMovie'
}
